import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Icon, IconName } from './icons';

export interface RowMenuItem {
  label: string;
  icon?: IconName;
  danger?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

interface Props {
  items: (RowMenuItem | 'separator')[];
  ariaLabel?: string;
}

export function RowMenu({ items, ariaLabel = 'Row actions' }: Props) {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: Event) => {
      if (!hostRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <div ref={hostRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className={'ts-menu-btn' + (open ? ' ts-menu-btn--on' : '')}
        onClick={toggle}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="dots" size={14} />
      </button>
      {open && (
        <div className="ts-popover" role="menu" onClick={(e) => e.stopPropagation()}>
          {items.map((it, i) => {
            if (it === 'separator') return <div key={i} className="ts-popover-sep" />;
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={'ts-popover-item' + (it.danger ? ' ts-popover-item--danger' : '')}
                disabled={it.disabled}
                onClick={() => { setOpen(false); it.onClick?.(); }}
              >
                {it.icon ? <Icon name={it.icon} size={13} /> : <span style={{ width: 13 }} />}
                <span>{it.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
